/**
 * Tenant permissions grouped by resource.
 * Each permission follows the format "resource:action".
 *
 * Mirrors the backend `TenantPermission` StrEnum, and must stay complete: the
 * role editor builds its checkboxes from this catalog and nothing else, so a
 * permission the backend enforces but this file omits cannot be granted at
 * all. A role with every box ticked still lacks it, and the endpoints behind
 * it answer 403 INSUFFICIENT_PERMISSIONS.
 */

export const PERMISSION_GROUPS = {
  user: {
    label: 'Gebruikers',
    permissions: {
      USER_CREATE: 'user:create',
      USER_READ: 'user:read',
      USER_UPDATE: 'user:update',
      USER_DELETE: 'user:delete',
      USER_MOVE: 'user:move',
    },
  },
  userRole: {
    label: 'Rollen',
    permissions: {
      USER_ROLE_CREATE: 'user_role:create',
      USER_ROLE_READ: 'user_role:read',
      USER_ROLE_UPDATE: 'user_role:update',
      USER_ROLE_DELETE: 'user_role:delete',
    },
  },
  auditLog: {
    label: 'Audit log',
    permissions: {
      AUDIT_LOG_READ: 'audit_log:read',
      AUDIT_LOG_READ_SENSITIVE: 'audit_log:read_sensitive',
    },
  },
  notificationLog: {
    label: 'Notificatielogs',
    permissions: {
      NOTIFICATION_LOG_READ: 'notification_log:read',
    },
  },
  unit: {
    label: 'Units',
    permissions: {
      UNIT_CREATE: 'unit:create',
      UNIT_READ: 'unit:read',
      UNIT_UPDATE: 'unit:update',
      UNIT_DELETE: 'unit:delete',
      UNIT_MOVE: 'unit:move',
    },
  },
  client: {
    label: 'Cliënten',
    permissions: {
      CLIENT_CREATE: 'client:create',
      CLIENT_READ: 'client:read',
      CLIENT_UPDATE: 'client:update',
      CLIENT_DELETE: 'client:delete',
      CLIENT_MOVE: 'client:move',
    },
  },
  clientEvent: {
    label: 'Planning',
    permissions: {
      CLIENT_EVENT_CREATE: 'client_event:create',
      CLIENT_EVENT_READ: 'client_event:read',
      CLIENT_EVENT_UPDATE: 'client_event:update',
      CLIENT_EVENT_DELETE: 'client_event:delete',
    },
  },
  interaction: {
    label: 'Interacties',
    permissions: {
      INTERACTION_CREATE: 'interaction:create',
      INTERACTION_READ: 'interaction:read',
      INTERACTION_UPDATE: 'interaction:update',
      INTERACTION_DELETE: 'interaction:delete',
      INTERACTION_PUBLISH: 'interaction:publish',
      INTERACTION_SHARE: 'interaction:share',
      INTERACTION_MOVE: 'interaction:move',
    },
  },
  device: {
    label: 'Apparaten',
    permissions: {
      DEVICE_READ: 'device:read',
      DEVICE_UPDATE: 'device:update',
    },
  },
  media: {
    label: 'Media',
    permissions: {
      MEDIA_CREATE: 'media:create',
      MEDIA_READ: 'media:read',
      MEDIA_DELETE: 'media:delete',
      MEDIA_SHARE: 'media:share',
    },
  },
  group: {
    label: 'Folders',
    permissions: {
      GROUP_CREATE: 'group:create',
      GROUP_READ: 'group:read',
      GROUP_UPDATE: 'group:update',
      GROUP_DELETE: 'group:delete',
    },
  },
  tag: {
    label: 'Tags',
    permissions: {
      TAG_CREATE: 'tag:create',
      TAG_READ: 'tag:read',
      TAG_UPDATE: 'tag:update',
      TAG_DELETE: 'tag:delete',
    },
  },
  contact: {
    label: 'Contacten',
    permissions: {
      CONTACT_CREATE: 'contact:create',
      CONTACT_READ: 'contact:read',
      CONTACT_UPDATE: 'contact:update',
      CONTACT_DELETE: 'contact:delete',
    },
  },
  download: {
    label: 'Downloads',
    permissions: {
      DOWNLOAD_READ: 'download:read',
    },
  },
  var: {
    label: 'Variabelen',
    permissions: {
      VAR_CREATE: 'var:create',
      VAR_READ: 'var:read',
      VAR_UPDATE: 'var:update',
      VAR_DELETE: 'var:delete',
    },
  },
  supportSession: {
    label: 'Supporttoegang',
    permissions: {
      SUPPORT_SESSION_CREATE: 'support_session:create',
      SUPPORT_SESSION_READ: 'support_session:read',
    },
  },
  scm: {
    label: 'Signalering',
    permissions: {
      SCM_CREATE: 'scm:create',
      SCM_READ: 'scm:read',
      SCM_UPDATE: 'scm:update',
      SCM_DELETE: 'scm:delete',
    },
  },
  aiAgentSettings: {
    label: 'AI-agent instellingen',
    permissions: {
      AI_AGENT_SETTINGS_READ: 'ai_agent_settings:read',
      AI_AGENT_SETTINGS_UPDATE: 'ai_agent_settings:update',
    },
  },
} as const;

/** All permission string values as a flat array */
export const ALL_PERMISSIONS: string[] = Object.values(PERMISSION_GROUPS).flatMap(
  (group) => Object.values(group.permissions)
);

/** Dutch labels for the action half of a permission (`tag:read` → "Lezen") */
export const ACTION_LABELS: Record<string, string> = {
  create: 'Aanmaken',
  read: 'Lezen',
  read_sensitive: 'Gevoelige gegevens inzien',
  update: 'Bewerken',
  delete: 'Verwijderen',
  move: 'Verplaatsen',
  publish: 'Publiceren',
  share: 'Delen',
  manage_modules: 'Modules beheren',
};

const GROUP_LABEL_BY_PERMISSION = new Map<string, string>(
  Object.values(PERMISSION_GROUPS).flatMap((group) =>
    Object.values(group.permissions).map(
      (permission) => [permission, group.label] as const
    )
  )
);

/**
 * How a permission is named to users, in hints and notices alike: its group
 * and action as the role editor shows them, e.g. "Tags lezen" for `tag:read`.
 * Falls back to the raw value for a permission outside the catalog.
 */
export function permissionLabel(permission: string): string {
  const group = GROUP_LABEL_BY_PERMISSION.get(permission);
  if (!group) return permission;
  const action = permission.split(':')[1];
  return `${group} ${(ACTION_LABELS[action] ?? action).toLowerCase()}`;
}

/** Permissions as a Dutch list of quoted labels: `"A"`, `"A" en "B"`, `"A", "B" en "C"`. */
export function permissionList(permissions: string[]): string {
  const labels = permissions.map((permission) => `"${permissionLabel(permission)}"`);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} en ${labels[labels.length - 1]}`;
}

/** Union type of all valid permission strings */
export type TenantPermission =
  (typeof PERMISSION_GROUPS)[keyof typeof PERMISSION_GROUPS]['permissions'][keyof (typeof PERMISSION_GROUPS)[keyof typeof PERMISSION_GROUPS]['permissions']];
